/**
 * One preset card: click to add, drag onto the graph (or onto a folder), edit, delete (click
 * twice). A read-only one (built-in, or a team you only view) offers "Duplicate" instead.
 */
import { Show, type JSX } from "solid-js";
import type { Preset } from "@protocol/Preset";
import { useApp } from "../../app/context";
import { KindShape, useNodeColor } from "../../shared/ui";
import { shortValues } from "../../shared/fields";

/** What a card shows and reports. */
export interface PresetCardProps {
  preset: Preset;
  /** Its library cannot be changed here: only duplicating it is offered. */
  readOnly: boolean;
  /** The delete button waits for its second click. */
  armed: boolean;
  /** The card was clicked: add its node. */
  onAdd: () => void;
  /** A press on the card (it may become a drag onto the graph). */
  onDragStart: (e: PointerEvent) => void;
  /** The delete button was clicked (once or twice). */
  onDelete: () => void;
}

/** A preset card. */
export function PresetCard(props: PresetCardProps): JSX.Element {
  const { presets, kinds } = useApp();
  const kind = () => kinds.ofNode(props.preset);
  const color = useNodeColor();
  return (
    <div class="pcard" data-preset={props.preset.id}>
      <button type="button" class="pmain" title="Click to add it, or drag it onto the graph" onPointerDown={(e) => props.onDragStart(e)} onClick={() => props.onAdd()}>
        <span class="pico" style={{ color: color(props.preset) }}>
          <KindShape shape={kind().shape} />
        </span>
        <span class="pname">{props.preset.label}</span>
        <span class="pmeta">
          {kind().label}
          {shortValues(props.preset).map((v) => ` · ${v}`).join("")}
        </span>
        <span class="pmeta">
          Creates {props.preset.name}
          {props.preset.links.length ? ` · ${props.preset.links.length} ${props.preset.links.length === 1 ? "link" : "links"}` : ""}
        </span>
      </button>
      <span class="pact">
        <Show
          when={!props.readOnly}
          fallback={
            <button type="button" class="mini" aria-label={`Duplicate preset ${props.preset.label}`} data-duplicate-preset={props.preset.id} onClick={() => presets.setDraft({ ...structuredClone(props.preset), id: "" })}>
              Duplicate
            </button>
          }
        >
          <button
            type="button"
            class="mini"
            aria-label={`Edit preset ${props.preset.label}`}
            data-edit-preset={props.preset.id}
            onClick={() => presets.setDraft(structuredClone({ ...props.preset }), presets.place().source)}
          >
            Edit
          </button>
          <button
            type="button"
            class="mini danger"
            classList={{ armed: props.armed }}
            aria-label={`Delete preset ${props.preset.label}`}
            data-delete-preset={props.preset.id}
            onClick={() => props.onDelete()}
          >
            {props.armed ? "Confirm" : "Delete"}
          </button>
        </Show>
      </span>
    </div>
  );
}
